import ClientUpdatesModule from "@/store/modules/clientUpdates.module";
import { toaster } from "@/utilities/toaster";
import { Guid } from "guid-typescript";
import { assign, Machine, send } from "xstate";
// A client with a single active team opens on it; with more, the user chooses one. Deactivated teams don't count.
const onlyActiveTeamId = (response) => {
    const teams = ((response && response.teams) || []).filter((t) => t.isActive !== false);
    return teams.length === 1 ? teams[0].id.toString() : null;
};
// The timeline loads only for a picked team
const hasClientAndTeam = (context) => context.clientId != null && !!context.teamId;
const defaultDateFrom = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date;
};
const updatesMachine = Machine({
    id: "updates",
    type: "parallel",
    context: {
        clientId: null,
        requestedClientId: null,
        teamId: null,
        dateFrom: defaultDateFrom(),
        dateTo: new Date(),
        activeTab: "all",
        timelineStale: true,
        updateFormMode: "add",
        savedUpdate: null,
    },
    states: {
        // Updates timeline - every tab (all/client/development/actionItems) filters the loaded timeline client-side.
        // A client's teams load first; its timeline loads only once a team is picked.
        timelineInfo: {
            initial: "idle",
            on: {
                SELECT_CLIENT: [
                    {
                        target: ".gettingTeams",
                        cond: (context, event) => event.clientId != null,
                        actions: ["assignClient", "clearTimeline"],
                    },
                    { target: ".idle", actions: ["assignClient", "clearTimeline"] },
                ],
                CHANGE_TEAM: [
                    {
                        target: ".gettingTimeline",
                        cond: (context, event) => context.clientId != null && !!event.teamId,
                        actions: "assignTeam",
                    },
                    // "Please select": no team, no updates
                    { target: ".idle", actions: ["assignTeam", "clearTimeline"] },
                ],
                CHANGE_DATE_RANGE: [
                    {
                        target: ".gettingTimeline",
                        cond: (context) => hasClientAndTeam(context),
                        actions: "assignDateRange",
                    },
                    { actions: "assignDateRange" },
                ],
                SELECT_TAB: [
                    {
                        target: ".gettingTimeline",
                        cond: (context) => context.timelineStale && hasClientAndTeam(context),
                        actions: "assignTab",
                    },
                    { actions: "assignTab" },
                ],
                REFRESH_TIMELINE: [
                    {
                        target: ".gettingTimeline",
                        cond: (context) => hasClientAndTeam(context),
                    },
                    { actions: "markTimelineStale" },
                ],
            },
            states: {
                idle: {},
                gettingTeams: {
                    // While the teams load, a team / date change is only recorded (Clear Filter sends client then team):
                    // the timeline loads once, with it, when the teams arrive
                    on: {
                        CHANGE_TEAM: { actions: "assignTeam" },
                        CHANGE_DATE_RANGE: { actions: "assignDateRange" },
                        REFRESH_TIMELINE: { actions: "markTimelineStale" },
                    },
                    invoke: {
                        id: "gettingTeams",
                        src: (context, event) => {
                            return ClientUpdatesModule.GetUpdatesTeams(context.clientId.toString());
                        },
                        onDone: [
                            { target: "gettingTimeline", cond: (context) => !!context.teamId },
                            // Every client load: a single active team is picked and its updates load; with more, "Choose team"
                            {
                                target: "gettingTimeline",
                                cond: (context, event) => onlyActiveTeamId(event.data) != null,
                                actions: "assignOnlyTeam",
                            },
                            { target: "idle" },
                        ],
                        onError: { target: "idle" },
                    },
                },
                gettingTimeline: {
                    invoke: {
                        id: "gettingTimeline",
                        src: (context, event) => {
                            return ClientUpdatesModule.GetUpdatesTimeline({ clientId: context.clientId, from: context.dateFrom, to: context.dateTo, teamId: context.teamId });
                        },
                        onDone: { target: "loadedTimeline", actions: "clearTimelineStale" },
                        onError: { target: "idle" },
                    },
                },
                loadedTimeline: {},
            },
        },
        // Add/Edit/Delete Update modal
        updateInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        ADD_UPDATE: { target: "updateFormOpen", actions: "setUpdateFormAdd" },
                        EDIT_UPDATE: { target: "updateFormOpen", actions: "setUpdateFormEdit" },
                        DELETE_UPDATE: { target: "deleteUpdateConfirm" },
                    },
                },
                updateFormOpen: {
                    on: {
                        SAVE_UPDATE: { target: "savingUpdate" },
                        CLOSE_MODAL: { target: "idle" },
                    },
                },
                savingUpdate: {
                    invoke: {
                        id: "savingUpdate",
                        src: async (context, event) => {
                            const response = context.updateFormMode === "edit"
                                ? await ClientUpdatesModule.EditUpdate(event.payLoad)
                                : await ClientUpdatesModule.AddUpdate(event.payLoad);
                            // Step 2: related items are saved against the persisted update. A failure here is already
                            // toasted by the api interceptor; the update itself is saved, so don't fail the whole save.
                            const updateId = response && response.update ? response.update.id.toString() : event.payLoad.id.toString();
                            // Removals first, then reopens, edits, completions, closes, then additions. Reopening before the
                            // edit lets a reopened item take a new assignee; completing / closing after it keeps the edited text.
                            for (const attachmentId of event.deletedAttachmentIds || []) {
                                try {
                                    await ClientUpdatesModule.DeleteAttachment(attachmentId);
                                }
                                catch (e) { }
                            }
                            for (const actionItemId of event.deletedActionItemIds || []) {
                                try {
                                    await ClientUpdatesModule.DeleteActionItem(actionItemId);
                                }
                                catch (e) { }
                            }
                            for (const actionItemId of event.reopenedActionItemIds || []) {
                                try {
                                    await ClientUpdatesModule.ReopenActionItem(actionItemId);
                                }
                                catch (e) { }
                            }
                            for (const item of event.editedActionItems || []) {
                                try {
                                    await ClientUpdatesModule.EditActionItem(item);
                                }
                                catch (e) { }
                            }
                            for (const actionItemId of event.completedActionItemIds || []) {
                                try {
                                    await ClientUpdatesModule.CompleteActionItem(actionItemId);
                                }
                                catch (e) { }
                            }
                            for (const actionItemId of event.closedActionItemIds || []) {
                                try {
                                    await ClientUpdatesModule.CloseActionItem(actionItemId);
                                }
                                catch (e) { }
                            }
                            for (const item of event.actionItems || []) {
                                try {
                                    await ClientUpdatesModule.AddActionItem({ ...item, updateId: updateId, clientId: item.clientId || event.payLoad.clientId });
                                }
                                catch (e) { }
                            }
                            for (const link of event.links || []) {
                                try {
                                    await ClientUpdatesModule.AddAttachmentLink({ updateId: updateId, url: link.url, label: link.label });
                                }
                                catch (e) { }
                            }
                            for (const file of event.files || []) {
                                try {
                                    await ClientUpdatesModule.UploadAttachmentFile({ updateId: updateId, file: file });
                                }
                                catch (e) { }
                            }
                            // Save (not Save & Close): reload the update with everything saved above, so the modal goes on
                            // editing it - new rows get their ids and the next Save edits instead of adding again.
                            // Reloaded by id because its Date may be outside the timeline's date range.
                            let savedUpdate = null;
                            if (!event.closeAfter) {
                                try {
                                    const summary = await ClientUpdatesModule.GetUpdateSummary(updateId);
                                    savedUpdate = summary && summary.update ? ClientUpdatesModule.updateSummary : null;
                                }
                                catch (e) { }
                            }
                            return { ...response, savedUpdate: savedUpdate };
                        },
                        onDone: [
                            {
                                target: "updateFormOpen",
                                cond: (context, event) => !!event.data.savedUpdate,
                                actions: ["assignSavedUpdate", "showSuccessMessage", "refreshTimeline"],
                            },
                            // Save & Close - and Save when the reload failed, as the modal can't safely save it again
                            { target: "idle", actions: ["showSuccessMessage", "refreshTimeline"] },
                        ],
                        onError: { target: "updateFormOpen" },
                    },
                },
                deleteUpdateConfirm: {
                    on: {
                        DELETING_UPDATE: { target: "deletingUpdate" },
                        CLOSE_MODAL: { target: "idle" },
                    },
                },
                deletingUpdate: {
                    invoke: {
                        id: "deletingUpdate",
                        src: (context, event) => {
                            return ClientUpdatesModule.DeleteUpdate(event.payLoad);
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "refreshTimeline"] },
                        onError: { target: "idle" },
                    },
                },
            },
        },
        // Add Action Item modal (existing action items are edited inside the Update modal)
        actionItemInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        ADD_ACTION_ITEM: { target: "actionItemFormOpen" },
                    },
                },
                actionItemFormOpen: {
                    on: {
                        SAVE_ACTION_ITEM: { target: "savingActionItem" },
                        CLOSE_ACTION_ITEM_MODAL: { target: "idle" },
                    },
                },
                savingActionItem: {
                    invoke: {
                        id: "savingActionItem",
                        src: (context, event) => {
                            return ClientUpdatesModule.AddActionItem(event.payLoad);
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "refreshTimeline"] },
                        onError: { target: "actionItemFormOpen" },
                    },
                },
            },
        },
        // Complete / reopen from the timeline - one item at a time, then the timeline is refetched
        actionItemStatusInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        SET_ACTION_ITEM_STATUS: { target: "savingActionItemStatus" },
                    },
                },
                savingActionItemStatus: {
                    invoke: {
                        id: "savingActionItemStatus",
                        src: (context, event) => {
                            return event.completed ? ClientUpdatesModule.CompleteActionItem(event.actionItemId) : ClientUpdatesModule.ReopenActionItem(event.actionItemId);
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "refreshTimeline"] },
                        onError: { target: "idle" },
                    },
                },
            },
        },
        // Page master data (clients + Interaction Types), loaded once on start. The first client is then selected
        // and its teams start loading in the same step; its only active team's timeline follows (several teams: the user chooses one).
        pageDataInfo: {
            initial: "gettingPageData",
            states: {
                gettingPageData: {
                    invoke: {
                        id: "gettingPageData",
                        src: (context, event) => {
                            return ClientUpdatesModule.GetUpdatesPageData();
                        },
                        onDone: [
                            {
                                target: ["loadedPageData", "#updates.timelineInfo.gettingTeams"],
                                cond: (context, event) => !!(event.data && event.data.clients && event.data.clients.length),
                                actions: "assignFirstClient",
                            },
                            { target: "loadedPageData" },
                        ],
                        onError: { target: "idle" },
                    },
                },
                loadedPageData: {},
                idle: {},
            },
        },
    },
}, {
    actions: {
        showSuccessMessage: (context, event) => {
            if (event.data && event.data.successMessages && event.data.successMessages.length) {
                toaster.ShowSuccess(event.data.successMessages[0]);
            }
        },
        // The requested client when it's in the list; otherwise the first active client
        // (a deactivated one only when every client is deactivated)
        assignFirstClient: assign({
            clientId: (context, event) => {
                const clients = event.data.clients;
                const requested = context.requestedClientId
                    ? clients.find((c) => c.id.toString().toLowerCase() === context.requestedClientId.toLowerCase())
                    : null;
                const first = requested || clients.find((c) => c.isActive !== false) || clients[0];
                return Guid.parse(first.id.toString());
            },
        }),
        // A team belongs to one client, so a new client starts with no team filter
        assignClient: assign({
            clientId: (context, event) => event.clientId,
            teamId: (context, event) => null,
        }),
        assignTeam: assign({
            teamId: (context, event) => event.teamId || null,
        }),
        assignDateRange: assign({
            dateFrom: (context, event) => event.dateFrom,
            dateTo: (context, event) => event.dateTo,
        }),
        assignTab: assign({
            activeTab: (context, event) => event.tab,
        }),
        markTimelineStale: assign({
            timelineStale: (context, event) => true,
        }),
        assignOnlyTeam: assign({
            teamId: (context, event) => onlyActiveTeamId(event.data),
        }),
        clearTimeline: (context, event) => {
            ClientUpdatesModule.ClearTimeline();
        },
        clearTimelineStale: assign({
            timelineStale: (context, event) => false,
        }),
        setUpdateFormAdd: assign({
            updateFormMode: (context, event) => "add",
            savedUpdate: (context, event) => null,
        }),
        setUpdateFormEdit: assign({
            updateFormMode: (context, event) => "edit",
            savedUpdate: (context, event) => null,
        }),
        // Once saved, the modal edits the update - a new one is never added twice
        assignSavedUpdate: assign({
            updateFormMode: (context, event) => "edit",
            savedUpdate: (context, event) => event.data.savedUpdate,
        }),
        // Refetch the timeline if a client is selected, otherwise mark it stale
        refreshTimeline: send({ type: "REFRESH_TIMELINE" }),
    },
    guards: {},
    activities: {},
    services: {},
});
export default updatesMachine;
